import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useAiAssistantPresentation} from '@dms/ai-assistant';
import {useReadCodeQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetRentalOrderQuery, useRentingStateQuery} from '@dms/api/rental';
import {useGetVehicleMileageQuery, useGetVehicleV2Query} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import {getCustomerName} from '@dms/shared';

import {toRentalOrderAssistantContext} from '../utils/toRentalOrderAssistantContext';
import {usePublishAssistantRentalOrderContext} from './usePublishAssistantRentalOrderContext';

export function useAssistantRentalOrderContext(rentalOrderId: string | undefined) {
  const formatDateTime = useDateTimeFormatter();
  const {isOpen} = useAiAssistantPresentation();
  const shouldFetch =
    isFeatureEnabled(featureFlags.OMNETIC_AI_ASSISTANT) && isOpen && isNotNil(rentalOrderId);
  const orderArgument = shouldFetch ? {rentalOrderId} : skipToken;
  const {currentData: rentalOrder, refetch: refetchOrder} = useGetRentalOrderQuery(orderArgument);
  const {currentData: rentingState, refetch: refetchRentingState} =
    useRentingStateQuery(orderArgument);
  const vehicleId = rentalOrder?.rentalVehicleId;
  const vehicleArgument = shouldFetch && isNotNil(vehicleId) ? {vehicleId} : skipToken;
  const {currentData: vehicle, refetch: refetchVehicle} = useGetVehicleV2Query(vehicleArgument);
  const {currentData: mileage, refetch: refetchMileage} =
    useGetVehicleMileageQuery(vehicleArgument);
  const customerId = rentalOrder?.customerId;
  const {currentData: customer, refetch: refetchCustomer} = useGetCustomerV2Query(
    shouldFetch && isNotNil(customerId) ? {customerId} : skipToken
  );
  const cancellationReasonId = rentalOrder?.cancellationReasonId;
  const shouldFetchReason =
    shouldFetch && rentalOrder?.state === 'canceled' && isNotNil(cancellationReasonId);
  const {currentData: cancellationReason, refetch: refetchReason} = useReadCodeQuery(
    shouldFetchReason ? {codelistId: 'cancellationReason', codeId: cancellationReasonId} : skipToken
  );
  const vehicleLabel = vehicle?.title;
  const customerLabel = getCustomerName(customer);
  const createContext = () =>
    shouldFetch
      ? toRentalOrderAssistantContext({
          formatDateTime,
          rentalOrderId,
          rentalOrder,
          vehicleLabel,
          customerLabel,
          mileage,
          rentingState,
          cancellationReason: cancellationReason?.name,
        })
      : undefined;
  const refreshContext = () => {
    if (!shouldFetch) {
      return Promise.resolve([]);
    }

    return Promise.all([
      refetchOrder(),
      refetchRentingState(),
      ...(isNil(vehicleId) ? [] : [refetchVehicle(), refetchMileage()]),
      ...(isNil(customerId) ? [] : [refetchCustomer()]),
      ...(shouldFetchReason ? [refetchReason()] : []),
    ]);
  };
  const refs = usePublishAssistantRentalOrderContext(createContext, refreshContext);

  return {...refs, vehicleLabel, customerLabel};
}
